import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Autocomplete,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SettingsIcon from "@mui/icons-material/Settings";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";

import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";

import axios from "axios";
import { Handle } from "react-flow-renderer";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { toast } from "react-toastify";

const HttpRequestAttachmentNode = React.memo(({ data, id, selected }) => {
  const [url, setUrl] = useState(data?.url || "");
  const [method, setMethod] = useState(data?.method || "GET");
  const [requestBody, setRequestBody] = useState(data?.requestBody || "{}");
  const [headersString, setHeadersString] = useState(data?.headersString || "");
  const [queryParams, setQueryParams] = useState(data?.queryParams || []);
  const [message, setMessage] = useState(data?.message || "");
  const [timeout, setTimeout] = useState(data?.timeout || 30000);
  const [savedStatus, setSavedStatus] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [testingRequest, setTestingRequest] = useState(false);

  const storageItems = useNodeStorage();

  // Sincronizar mudanças com data
  useEffect(() => {
    if (data) {
      data.url = url;
      data.method = method;
      data.requestBody = requestBody;
      data.headersString = headersString;
      data.queryParams = queryParams;
      data.message = message;
      data.timeout = timeout;
    }
  }, [url, method, requestBody, headersString, queryParams, message, timeout, data]);

  const handleSave = () => {
    if (!url) {
      toast.error("URL é obrigatória");
      return;
    }
    if (!message) {
      toast.error("Mensagem é obrigatória");
      return;
    }

    setSavedStatus('Configuração salva!');
    setTimeout(() => setSavedStatus(''), 3000);
    setShowDialog(false);
    toast.success("Configuração do nó HTTP Request Anexo salva com sucesso!");
  };

  const handleTestRequest = async () => {
    if (!url) {
      toast.error("URL é obrigatória para testar");
      return;
    }

    setTestingRequest(true);
    try {
      let headers = {};
      try {
        if (headersString) {
          headers = JSON.parse(headersString);
        }
      } catch (err) {
        toast.error("Headers inválido (JSON malformado)");
        setTestingRequest(false);
        return;
      }

      let body = null;
      if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method?.toUpperCase())) {
        try {
          body = requestBody ? JSON.parse(requestBody) : null;
        } catch (err) {
          body = requestBody;
        }
      }

      // Construir URL com query params
      let fullUrl = url;
      if (queryParams && queryParams.length > 0) {
        const params = new URLSearchParams();
        queryParams.forEach(param => {
          if (param.key) {
            params.append(param.key, param.value || '');
          }
        });
        const queryString = params.toString();
        if (queryString) {
          fullUrl += (url.includes('?') ? '&' : '?') + queryString;
        }
      }

      const response = await axios({
        method: method || 'GET',
        url: fullUrl,
        headers: headers,
        data: body,
        timeout: timeout || 30000,
        responseType: 'blob'
      });

      // Verificar se é um PDF
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/pdf')) {
        toast.success(`Requisição bem-sucedida! PDF recebido (${(response.data.size / 1024).toFixed(2)} KB)`);
      } else {
        toast.warning(`Requisição bem-sucedida, mas o tipo de conteúdo não é PDF: ${contentType}`);
      }
    } catch (error) {
      console.error("Erro ao testar requisição:", error);
      toast.error(`Erro ao testar requisição: ${error.message}`);
    } finally {
      setTestingRequest(false);
    }
  };

  const addQueryParam = () => {
    setQueryParams([...queryParams, { key: "", value: "" }]);
  };

  const removeQueryParam = (index) => {
    const newParams = queryParams.filter((_, i) => i !== index);
    setQueryParams(newParams);
  };

  const updateQueryParam = (index, field, value) => {
    const newParams = [...queryParams];
    newParams[index][field] = value;
    setQueryParams(newParams);
  };

  return (
    <>
      <div
        style={{
          backgroundColor: "#2d3748",
          padding: "12px",
          borderRadius: "8px",
          minWidth: "200px",
          border: selected ? "2px solid #9a00ed" : "2px solid transparent"
        }}
      >
        <Handle
          type="target"
          position="left"
          style={{
            background: "#9a00ed",
            width: "18px",
            height: "18px",
            top: "20px",
            left: "-12px",
            cursor: 'pointer'
          }}
          isConnectable={true}
        >
          <ArrowForwardIos
            sx={{
              color: "#ffff",
              width: "10px",
              height: "10px",
              marginLeft: "3.5px",
              marginBottom: "1px",
              pointerEvents: "none"
            }}
          />
        </Handle>

        <div
          style={{
            display: "flex",
            position: "absolute",
            right: 8,
            top: 8,
            cursor: "pointer",
            gap: 6
          }}
        >
          <ContentCopyIcon
            onClick={() => {
              storageItems.setNodesStorage(id);
              storageItems.setAct("duplicate");
            }}
            sx={{ width: "14px", height: "14px", color: "#fff", cursor: 'pointer' }}
          />

          <DeleteOutlineIcon
            onClick={() => {
              storageItems.setNodesStorage(id);
              storageItems.setAct("delete");
            }}
            sx={{ width: "14px", height: "14px", color: "#fff", cursor: 'pointer' }}
          />
        </div>

        <div
          style={{
            color: "#fff",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            marginBottom: "8px",
            gap: "6px"
          }}
        >
          <AttachFileIcon sx={{ width: "18px", height: "18px" }} />
          <PictureAsPdfIcon sx={{ width: "18px", height: "18px" }} />
          HTTP Request Anexo
        </div>

        <div style={{ color: "#cbd5e0", fontSize: "11px", marginBottom: "8px" }}>
          {method || "GET"} {url ? `${url.substring(0, 30)}...` : "URL não configurada"}
        </div>

        {message && (
          <div style={{ color: "#a0aec0", fontSize: "10px", marginBottom: "6px", fontStyle: "italic" }}>
            📝 {message.substring(0, 40)}{message.length > 40 ? "..." : ""}
          </div>
        )}

        <Button
          variant="outlined"
          size="small"
          startIcon={<SettingsIcon />}
          onClick={() => setShowDialog(true)}
          sx={{
            color: "#fff",
            borderColor: "#9a00ed",
            fontSize: "10px",
            textTransform: "none",
            marginTop: "4px",
            '&:hover': {
              borderColor: "#b84eff",
              backgroundColor: "rgba(154, 0, 237, 0.1)"
            }
          }}
        >
          Configurar
        </Button>

        {savedStatus && (
          <div style={{ color: "#48bb78", fontSize: "9px", marginTop: "4px" }}>
            ✓ {savedStatus}
          </div>
        )}

        {/* Handle de Sucesso */}
        <Handle
          type="source"
          position="right"
          id="success"
          style={{
            background: "#48bb78",
            width: "18px",
            height: "18px",
            top: "40%",
            right: "-11px",
            cursor: 'pointer',
            border: "2px solid #2f855a"
          }}
          isConnectable={true}
        >
          <ArrowForwardIos
            sx={{
              color: "#ffff",
              width: "10px",
              height: "10px",
              marginLeft: "2.9px",
              marginTop: "2.6px"
            }}
          />
        </Handle>

        {/* Tooltip Sucesso */}
        <div
          style={{
            position: "absolute",
            right: "-70px",
            top: "38%",
            fontSize: "9px",
            color: "#48bb78",
            fontWeight: "600",
            pointerEvents: "none",
            whiteSpace: "nowrap"
          }}
        >
          ✓ Sucesso
        </div>

        {/* Handle de Erro */}
        <Handle
          type="source"
          position="right"
          id="error"
          style={{
            background: "#f56565",
            width: "18px",
            height: "18px",
            top: "60%",
            right: "-11px",
            cursor: 'pointer',
            border: "2px solid #c53030"
          }}
          isConnectable={true}
        >
          <ArrowForwardIos
            sx={{
              color: "#ffff",
              width: "10px",
              height: "10px",
              marginLeft: "2.9px",
              marginTop: "2.6px"
            }}
          />
        </Handle>

        {/* Tooltip Erro */}
        <div
          style={{
            position: "absolute",
            right: "-60px",
            top: "58%",
            fontSize: "9px",
            color: "#f56565",
            fontWeight: "600",
            pointerEvents: "none",
            whiteSpace: "nowrap"
          }}
        >
          ✕ Erro
        </div>
      </div>

      {/* Dialog de Configuração */}
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <AttachFileIcon />
              <PictureAsPdfIcon />
              Configurar HTTP Request Anexo
            </Box>
            <IconButton onClick={() => setShowDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* URL e Método */}
            <Grid item xs={12} md={9}>
              <TextField
                fullWidth
                label="URL da Requisição"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.exemplo.com/gerar-pdf"
                variant="outlined"
                size="small"
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Método</InputLabel>
                <Select
                  value={method}
                  onChange={(e) => setMethod(e.target.value)}
                  label="Método"
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Mensagem */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Mensagem para enviar com o PDF"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Segue o documento solicitado em anexo"
                variant="outlined"
                size="small"
                multiline
                rows={2}
                required
                helperText="Esta mensagem será enviada junto com o arquivo PDF"
              />
            </Grid>

            {/* Query Parameters */}
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2">Query Parameters</Typography>
                <Button
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={addQueryParam}
                >
                  Adicionar
                </Button>
              </Box>
              {queryParams && queryParams.map((param, index) => (
                <Box key={index} display="flex" gap={1} mb={1}>
                  <TextField
                    size="small"
                    label="Chave"
                    value={param.key}
                    onChange={(e) => updateQueryParam(index, 'key', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Valor"
                    value={param.value}
                    onChange={(e) => updateQueryParam(index, 'value', e.target.value)}
                    fullWidth
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeQueryParam(index)}
                    color="error"
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Box>
              ))}
            </Grid>

            {/* Headers */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Headers (JSON)
              </Typography>
              <AceEditor
                mode="json"
                theme="github"
                value={headersString}
                onChange={setHeadersString}
                name="headers-editor"
                width="100%"
                height="120px"
                fontSize={13}
                showPrintMargin={false}
                showGutter={true}
                highlightActiveLine={true}
                setOptions={{
                  useWorker: false,
                  showLineNumbers: true,
                  tabSize: 2,
                }}
              />
              <Typography variant="caption" color="textSecondary">
                Exemplo: {`{"Authorization": "Bearer token", "Content-Type": "application/json"}`}
              </Typography>
            </Grid>

            {/* Body (se não for GET) */}
            {method && !['GET', 'HEAD'].includes(method.toUpperCase()) && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Body (JSON)
                </Typography>
                <AceEditor
                  mode="json"
                  theme="github"
                  value={requestBody}
                  onChange={setRequestBody}
                  name="body-editor"
                  width="100%"
                  height="150px"
                  fontSize={13}
                  showPrintMargin={false}
                  showGutter={true}
                  highlightActiveLine={true}
                  setOptions={{
                    useWorker: false,
                    showLineNumbers: true,
                    tabSize: 2,
                  }}
                />
              </Grid>
            )}

            {/* Timeout */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Timeout (ms)"
                value={timeout}
                onChange={(e) => setTimeout(parseInt(e.target.value))}
                variant="outlined"
                size="small"
                helperText="Tempo máximo de espera pela resposta"
              />
            </Grid>

            {/* Botão de Teste */}
            <Grid item xs={12}>
              <Alert severity="info">
                Este nó fará uma requisição HTTP que deve retornar um arquivo PDF (Content-Type: application/pdf).
                O PDF será baixado e enviado como anexo junto com a mensagem configurada.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleTestRequest}
            startIcon={<PlayArrowIcon />}
            disabled={testingRequest || !url}
            variant="outlined"
          >
            {testingRequest ? "Testando..." : "Testar Requisição"}
          </Button>
          <Button onClick={() => setShowDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            startIcon={<SaveAltIcon />}
            disabled={!url || !message}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default HttpRequestAttachmentNode;
